import { type QueryClient, type QueryKey } from "@tanstack/react-query";

/** Keep route drops serialized until both the write and list refresh finish. */
export async function saveRouteGroupDrop<T>({
  queryClient,
  queryKey,
  routeId,
  pendingRoute,
  onPendingChange,
  updateRoutes,
  save,
}: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  routeId: string;
  pendingRoute: { current: string | null };
  onPendingChange: (routeId: string | null) => void;
  updateRoutes: (routes: T[] | undefined) => T[] | undefined;
  save: () => Promise<unknown>;
}): Promise<{ refreshed: boolean } | null> {
  if (pendingRoute.current !== null) return null;
  pendingRoute.current = routeId;
  onPendingChange(routeId);
  try {
    await queryClient.cancelQueries({ queryKey });
    const previousRoutes = queryClient.getQueryData<T[]>(queryKey);
    queryClient.setQueryData<T[]>(queryKey, updateRoutes);
    const [write] = await Promise.allSettled([Promise.resolve().then(save)]);
    if (write.status === "rejected") {
      queryClient.setQueryData(queryKey, previousRoutes);
    }
    // A failed response can follow a committed write. Reload either way.
    let refreshed = true;
    try {
      await queryClient.invalidateQueries({ queryKey }, { throwOnError: true });
      // Invalidation returns early when offline; wait for the queued reload.
      const query = queryClient.getQueryCache().find({ queryKey, exact: true });
      if (query?.state.fetchStatus === "paused") await query.promise;
    } catch {
      refreshed = false;
    }
    if (write.status === "rejected") throw write.reason;
    return { refreshed };
  } finally {
    pendingRoute.current = null;
    onPendingChange(null);
  }
}

export type GroupDragResult =
  | { action: "move"; sourcePath: string | null; targetPath: string | null }
  | { action: "reorder"; targetRouteId: string }
  | null;

/**
 * Decide how a route drag should resolve. Group/season headers win over
 * nested sortable projection so hovering a parent does not land in a child.
 */
export function resolveGroupDragEnd({
  sourcePath,
  sourceRouteId,
  targetType,
  targetPath,
  targetRouteId,
  targetRoutePath,
  projectedPath,
  initialPath,
  projectedIndex,
  routesInInitialGroup,
}: {
  sourcePath: string | null;
  sourceRouteId: string;
  targetType?: string;
  targetPath?: string | null;
  targetRouteId?: string;
  targetRoutePath?: string | null;
  projectedPath?: string | null;
  initialPath?: string | null;
  projectedIndex?: number;
  routesInInitialGroup: Array<{ _id: string }>;
}): GroupDragResult {
  if (targetType === "route-group") {
    const nextPath = targetPath ?? null;
    if (sourcePath === nextPath) return null;
    return { action: "move", sourcePath, targetPath: nextPath };
  }

  const hasProjection =
    projectedPath !== undefined &&
    initialPath !== undefined &&
    typeof projectedIndex === "number";

  if (hasProjection) {
    if (projectedPath !== initialPath) {
      return {
        action: "move",
        sourcePath: initialPath,
        targetPath: projectedPath ?? null,
      };
    }
    const targetRoute = routesInInitialGroup[projectedIndex];
    if (targetRoute && targetRoute._id !== sourceRouteId) {
      return { action: "reorder", targetRouteId: targetRoute._id };
    }
  }

  if (targetType === "group-route" && targetRouteId) {
    const nextPath = targetRoutePath ?? null;
    if (sourcePath === nextPath) {
      if (sourceRouteId !== targetRouteId) {
        return { action: "reorder", targetRouteId };
      }
      return null;
    }
    return { action: "move", sourcePath, targetPath: nextPath };
  }

  return null;
}
